import * as actionTypes from "./actionTypes";
import {
  getIssueVacationsApi,
  deleteIssueVacationAPI,
  createIssueVacationApi,
  updateIssueVacationApi,
  importIssueVacationsExcelApi,
  downloadIssueVacationsExcelApi,
  generateFileFromIssueVacationApi,
} from "@icarius-connection/api";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";
import { getFileExtension, getFileName } from "@icarius-utils/fileUpload";
import { downloadBlob } from "@icarius-utils/download";

const errorHandler = (e, dispatch) => {
  let errorString = "";
  switch (e.response?.data?.status) {
    case "INVALID_DATA":
    case "VALIDATION_ERROR":
      errorString = getLocalizedString("validationError");
      break;
    case "IN_USE":
      errorString = getLocalizedString("cannotDeleteVacationsBecauseTheyAreInUse");
      break;
    case "DUPLICATED_DATA":
      errorString = getLocalizedString("incidentsAlreadyExistInEnteredDatesVacationsCannotBeSaved");
      break;
    default:
      errorString = getErrorStringFromServerError(e);
      break;
  }
  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

export const getIssueVacationsAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.GET });
  try {
    let response = await getIssueVacationsApi();

    let data = response.data && response.data.data;
    let selects = response.data && response.data.selects;
    let status = response.data && response.data.status;
    let startHolidaysOnNonWorkingDay = response.data && response.data.startHolidaysOnNonWorkingDay;
    let allowHalfDayHolidays = response.data && response.data.allowHalfDayHolidays;
    let additionalLegalDays = response.data && response.data.additional_legal_days;
    let hasSignedDocument = response.data && response.data.hasSignedDocument;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_FULFILLED,
      payload: {
        data,
        selects,
        startHolidaysOnNonWorkingDay,
        allowHalfDayHolidays,
        additionalLegalDays,
        hasSignedDocument,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const deleteIssueVacationAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE });
  try {
    let response = await deleteIssueVacationAPI(dataToSend);

    let data = response.data && response.data.data;
    let selects = response.data && response.data.selects;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_FULFILLED,
      payload: { data, selects },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("vacationsDeletedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const createIssueVacationAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE });
  try {
    let response = await createIssueVacationApi(dataToSend);

    let data = response.data && response.data.data;
    let selects = response.data && response.data.selects;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_FULFILLED,
      payload: { data, selects },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("vacationsCreatedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const updateIssueVacationAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.MODIFY });
  try {
    let response = await updateIssueVacationApi(dataToSend);

    let data = response.data && response.data.data;
    let selects = response.data && response.data.selects;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.MODIFY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.MODIFY_FULFILLED,
      payload: { data, selects },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("vacationsUpdatedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.MODIFY_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const downloadStructureAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.DOWNLOAD });
  try {
    let response = await downloadIssueVacationsExcelApi({ type: "VACATIONS", empty: false });
    let title = response.headers["x-content-namefile"];

    if (response.status !== 200) {
      dispatch({ type: actionTypes.DOWNLOAD_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DOWNLOAD_FULFILLED,
    });

    downloadBlob(response.data, getFileExtension(title), getFileName(title));

    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DOWNLOAD_REJECTED });
    dispatch(openSnackbarAction({ msg: getErrorStringFromServerError(e), severity: "error" }));
  }
};

export const importSpreadsheetAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPLOAD });
  try {
    let formData = new FormData();
    formData.append('file', dataToSend.file);

    let response = await importIssueVacationsExcelApi(formData);
    let title = response.headers["x-content-namefile"];
    let hasErrors = title && title.includes("Errores-");
    if (title) {
      title = title.replace("Errores-", "");
    }
    if (response.status !== 200) {
      dispatch({ type: actionTypes.UPLOAD_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPLOAD_FULFILLED,
    });

    if (title) {
      downloadBlob(response.data, getFileExtension(title), getFileName(title));
      dispatch(openSnackbarAction({
        msg: getLocalizedString("importFinishedCheckFileForResult"),
        severity: !hasErrors ? "success" : "warning",
        duration: null
      }));
      return response;
    }
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPLOAD_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const generateFileFromIssueVacationAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.GENERATE });
  try {
    let response = await generateFileFromIssueVacationApi(dataToSend);
    let reception = response.data && response.data.reception;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GENERATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GENERATE_FULFILLED,
    });

    dispatch(openSnackbarAction({
      msg:
        reception
          ? getLocalizedString("documentGeneratedSuccessfullyAndSentForEmployeeReceipt")
          : getLocalizedString("documentGeneratedSuccessfullyAndSentToEmployeeDigitalFolder"),
      severity: "success"
    }));

    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GENERATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};